import { Images } from '@/assets/images';
import { DocTypeEnum } from '@/constant';
import {
	cn,
	convertProductionTypeCodeToName,
	dayjs,
	formatDate,
} from '@/utils';

import * as Yup from 'yup';

import { FormConfig } from '../define-document-write.type';

const initializeValue = {
	appcNational: '',
	appcName: '',
	appcPassportNo: '',
	appcAlienRegistNo: '',
	appcDepartureDate: '',

	appcContractStartDate: '',
	appcContractEndDate: '',

	franchiseName: '',
	franchiseAddress: '',
	franchiseEmployeer: '',
	franchiseTelNo: '',
	businessRegisNo: '',
	franchiseType: '',

	letterRecomExtOfStayName: '',
	position: '', // '시장 (군수, 구청장)',
	letterRecomExtOfStayNameEn: '',
	positionEn: '', // 'City (Gun, Gu)',
	letterRecomExtOfStayRegistDate: '',
};

type IValues = typeof initializeValue;

export const RENEW_STAY_RECOMMENDATION_CONFIG: FormConfig<
	IValues,
	DocTypeEnum.ES1_10
> = {
	initializeValue,
	title: '체류기간 연장 추천서',
	mapPropToValues: ({
		detailData,
		franchiseData,
		applicantData,
		applicantAssignmentListData,
		unitData,
	}) => {
		return {
			appcNational: applicantData?.nationalName || '',
			appcName: applicantData?.fullName || '',
			appcPassportNo: applicantData?.passportNo || '',
			appcAlienRegistNo: applicantData?.foreignerNo || '',
			appcDepartureDate: applicantData?.departureDate || '',

			franchiseName: franchiseData?.name || '',
			franchiseAddress: franchiseData?.address || '',
			franchiseEmployeer: franchiseData?.employerName || '',
			franchiseTelNo: franchiseData?.employerPhone || '',
			businessRegisNo: franchiseData?.employerBirthDate
				? `${formatDate(franchiseData?.employerBirthDate, {
						outputFormat: 'YYMMDD',
					})}-`
				: '',
			franchiseType: `${convertProductionTypeCodeToName(franchiseData?.productionType) || ''}${franchiseData?.productionTypeDetail ? `(${franchiseData.productionTypeDetail})` : ''}`,

			letterRecomExtOfStayName: unitData?.name
				? unitData?.name
				: detailData?.unitName || '',
			letterRecomExtOfStayNameEn: unitData?.name
				? unitData?.name
				: detailData?.unitName || '',

			appcContractStartDate: franchiseData?.startDate
				? formatDate(franchiseData?.startDate)
				: formatDate(applicantAssignmentListData?.[0]?.startDate),
			appcContractEndDate: franchiseData?.endDate
				? formatDate(franchiseData?.endDate)
				: formatDate(applicantAssignmentListData?.[0]?.endDate),

			letterRecomExtOfStayRegistDate: formatDate(dayjs()),
		};
	},
	pages: [
		{
			background: {
				src: Images.renewStayRecommendation,
				className: 'template',
				dataSource: Images.renewStayRecommendation,
			},
			textAreas: [
				{
					name: 'appcNational',
					maxHeight: 24,
					maxLength: 30,
					containerClassName: 'absolute top-[208px] left-[84px] w-[315px]',
				},
				{
					name: 'appcName',
					maxHeight: 24,
					maxLength: 30,
					containerClassName: 'absolute top-[208px] left-[402px] w-[315px]',
				},
				{
					name: 'appcPassportNo',
					maxHeight: 24,
					maxLength: 20,
					containerClassName: 'absolute top-[253px] left-[84px] w-[315px]',
				},
				{
					name: 'appcAlienRegistNo',
					maxHeight: 24,
					maxLength: 20,
					containerClassName: 'absolute top-[253px] left-[402px] w-[315px]',
				},
				{
					name: 'franchiseName',
					maxHeight: 24,
					maxLength: 30,
					containerClassName: 'absolute top-[398px] left-[84px] w-[315px]',
				},
				{
					name: 'franchiseAddress',
					maxHeight: 24,
					maxLength: 100,
					className: 'leading-[12px]',
					containerClassName: 'absolute top-[398px] left-[402px] w-[315px]',
				},
				{
					name: 'franchiseEmployeer',
					maxHeight: 24,
					maxLength: 30,
					containerClassName: 'absolute top-[444px] left-[84px] w-[315px]',
				},
				{
					name: 'franchiseTelNo',
					maxHeight: 24,
					maxLength: 20,
					containerClassName: 'absolute top-[444px] left-[402px] w-[315px]',
				},
				{
					name: 'businessRegisNo',
					maxHeight: 24,
					maxLength: 20,
					containerClassName: 'absolute top-[492px] left-[84px] w-[315px]',
				},
				{
					name: 'franchiseType',
					maxHeight: 24,
					maxLength: 30,
					containerClassName: 'absolute top-[492px] left-[402px] w-[315px]',
				},
				(values, index) => ({
					name: 'letterRecomExtOfStayName',
					maxHeight: 32,
					maxLength: 20,
					className: 'text-center text-[15px] focus:bg-white bg-transparent',
					containerClassName: cn(
						'absolute left-[154px] top-[836px] w-[334px] z-[1]',
						values?.letterRecomExtOfStayName && 'bg-white'
					),
				}),
				(values, index) => ({
					name: 'position',
					maxHeight: 26,
					maxLength: 20,
					className: 'text-[16px] text-left',
					containerClassName: cn(
						'absolute top-[840px] left-[279px] w-[210px]',
						values?.letterRecomExtOfStayName && 'bg-white'
					),
				}),
				(values, index) => ({
					name: 'letterRecomExtOfStayNameEn',
					maxHeight: 48,
					maxLength: 20,
					className: 'text-[16px] text-center focus:bg-white bg-transparent',
					containerClassName: cn(
						'absolute top-[864px] left-[294px] w-[232px] z-[1]',
						values?.letterRecomExtOfStayNameEn && 'bg-white'
					),
				}),
				(values, index) => ({
					name: 'positionEn',
					maxHeight: 26,
					maxLength: 13,
					className: 'text-[16px] text-left',
					containerClassName: cn(
						'absolute top-[873px] left-[362px] w-[162px]',
						values?.letterRecomExtOfStayNameEn && 'bg-white'
					),
					readOnly: true,
				}),
			],
			datePickers: [
				{
					name: 'appcDepartureDate',
					className:
						'[&>.ant-picker-input>input]:!text-[14px] [&>.ant-picker-input>input]:text-left',
					containerClassName: 'absolute top-[297px] left-[397px]',
					style: {
						fontSize: '14px',
						width: '340px',
						textAlign: 'left',
					},
				},
				{
					name: 'letterRecomExtOfStayRegistDate',
					segmentedInputs: true,
					containerClassName: 'absolute top-[738px] left-[275px]',
					format: 'YYYY                     MM                           DD',
					style: { fontSize: '14px' },
				},
			],
			rangePickers: [
				{
					name: ['appcContractStartDate', 'appcContractEndDate'],
					containerClassName:
						'absolute top-[295px] left-[84px] w-[306px] h-[24px] [&>.ant-picker-range>.ant-picker-input>input]:!text-[14px]',
					style: {
						fontSize: '14px',
					},
				},
			],
			// inputFiles: [
			// 	{
			// 		name: 'signatureFilePath',
			// 		className:
			// 			'absolute top-[829px] left-[530px] w-[85px] h-[85px] pointer-events-none',
			// 	},
			// ],
			cachetForms: [
				{
					className:
						'absolute top-[829px] left-[530px] w-[85px] h-[85px] bg-white',
				},
			],
			highlightBoxes: [
				{
					name: 'appcNational',
					className: 'top-[194px] left-[78px] w-[110px] !h-[16px]',
				},
				{
					name: 'appcName',
					className: 'top-[194px] left-[399px] w-[127px] !h-[16px]',
				},
				{
					name: 'appcPassportNo',
					className: 'top-[237px] left-[78px] w-[158px] !h-[16px]',
				},
				{
					name: 'appcAlienRegistNo',
					className: 'top-[237px] left-[399px] w-[291px] !h-[18px]',
				},
				{
					names: ['appcContractStartDate', 'appcContractEndDate'],
					className: 'top-[280px] left-[78px] w-[242px] !h-[18px]',
				},
				{
					name: 'appcDepartureDate',
					className: 'top-[280px] left-[399px] w-[265px] !h-[18px]',
				},
				{
					name: 'franchiseName',
					className: 'top-[379px] left-[78px] w-[199px] !h-[18px]',
				},
				{
					name: 'franchiseAddress',
					className: 'top-[379px] left-[399px] w-[108px] !h-[18px]',
				},
				{
					name: 'franchiseEmployeer',
					className: 'top-[427px] left-[78px] w-[118px] !h-[18px]',
				},
				{
					name: 'franchiseTelNo',
					className: 'top-[427px] left-[399px] w-[164px] !h-[18px]',
				},
				(values, index) => ({
					name: 'businessRegisNo',
					className: 'top-[473px] left-[78px] w-[282px] !h-[18px]',
					hasError: values?.businessRegisNo?.length < 13,
				}),
				{
					name: 'franchiseType',
					className: 'top-[473px] left-[399px] w-[130px] !h-[18px]',
				},
				{
					name: 'letterRecomExtOfStayRegistDate',
					className: 'top-[740px] left-[258px] w-[300px] !h-[18px]',
				},
				{
					name: 'letterRecomExtOfStayName',
					className: 'top-[844px] left-[156px] w-[122px] !h-[18px]',
				},
				{
					name: 'letterRecomExtOfStayNameEn',
					className: 'top-[877px] left-[295px] w-[67px] !h-[18px]',
				},
			],
		},
	],
	validationSchema: Yup.object().shape({
		appcNational: Yup.string().required(),
		appcName: Yup.string().required(),
		appcPassportNo: Yup.string().required(),
		appcAlienRegistNo: Yup.string().required(),
		appcDepartureDate: Yup.string().required(),

		appcContractStartDate: Yup.string().required(),
		appcContractEndDate: Yup.string().required(),

		franchiseName: Yup.string().required(),
		franchiseAddress: Yup.string().required(),
		franchiseEmployeer: Yup.string().required(),
		franchiseTelNo: Yup.string().required(),
		businessRegisNo: Yup.string().required().min(13),
		franchiseType: Yup.string().required(),
		letterRecomExtOfStayName: Yup.string().required(),
		letterRecomExtOfStayNameEn: Yup.string().required(),
		letterRecomExtOfStayRegistDate: Yup.string().required(),
	}),
};
